export interface WheelReloadState {
  accumulatedDelta: number;
  direction: "up" | "down" | null;
  lastEventAt: number;
  cooldownUntil: number;
}
const WHEEL_SEQUENCE_GAP_MS = 300;
const WHEEL_RELOAD_THRESHOLD = 140;
const WHEEL_RELOAD_COOLDOWN_MS = 1_000;
const WHEEL_LINE_HEIGHT_PX = 16;
export const WHEEL_DELTA_MODE_PIXEL = 0;
export const WHEEL_DELTA_MODE_LINE = 1;
const WHEEL_DELTA_MODE_PAGE = 2;

export const initialWheelReloadState = (): WheelReloadState => ({
  accumulatedDelta: 0,
  direction: null,
  lastEventAt: 0,
  cooldownUntil: 0,
});

export function resetWheelReloadSequence(
  state: WheelReloadState,
  now: number = performance.now(),
): WheelReloadState {
  return {
    ...state,
    accumulatedDelta: 0,
    direction: null,
    lastEventAt: now,
  };
}

export function recordEdgeWheel(
  state: WheelReloadState,
  deltaY: number,
  atEdge: boolean,
  now: number = performance.now(),
  deltaMode: number = WHEEL_DELTA_MODE_PIXEL,
): { state: WheelReloadState; reload: boolean } {
  if (now < state.cooldownUntil)
    return {
      state: {
        ...resetWheelReloadSequence(state, now),
        // Keep one inertial wheel sequence from rearming after a fixed delay.
        cooldownUntil: now + WHEEL_RELOAD_COOLDOWN_MS,
      },
      reload: false,
    };
  if (!atEdge || deltaY === 0)
    return {
      state: resetWheelReloadSequence(state, now),
      reload: false,
    };
  const direction = deltaY > 0 ? "down" : "up";
  const continuesSequence =
    state.direction === direction &&
    now - state.lastEventAt <= WHEEL_SEQUENCE_GAP_MS;
  const accumulatedDelta =
    (continuesSequence ? state.accumulatedDelta : 0) +
    normalizeWheelDelta(deltaY, deltaMode);
  if (accumulatedDelta >= WHEEL_RELOAD_THRESHOLD)
    return {
      state: {
        accumulatedDelta: 0,
        direction: null,
        lastEventAt: now,
        cooldownUntil: now + WHEEL_RELOAD_COOLDOWN_MS,
      },
      reload: true,
    };
  return {
    state: {
      ...state,
      accumulatedDelta,
      direction,
      lastEventAt: now,
    },
    reload: false,
  };
}

function normalizeWheelDelta(deltaY: number, deltaMode: number): number {
  const magnitude = Math.abs(deltaY);
  if (deltaMode === WHEEL_DELTA_MODE_LINE) {
    return magnitude * WHEEL_LINE_HEIGHT_PX;
  }
  if (deltaMode === WHEEL_DELTA_MODE_PAGE) {
    return magnitude * WHEEL_RELOAD_THRESHOLD;
  }
  return magnitude;
}
